'use client';

import { useState, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils/cn';
import { useI18n } from '@/lib/i18n/context';

interface PinCardProps {
  id: string;
  src: string;
  thumb?: string;
  title: string;
  tag?: string;
  author?: { name: string; username?: string; avatar?: string };
  views?: number;
  likes?: number;
  isLiked?: boolean;
  isSaved?: boolean;
  priority?: boolean;
  // Optional: aspect ratio hint để giữ không gian trước khi ảnh load
  aspectRatio?: number; // width / height, vd: 0.75 = 3:4
}

export function PinCard({
  id, src, thumb, title, tag, author,
  views = 0, likes = 0, isLiked = false, isSaved = false,
  priority = false,
  aspectRatio = 0.75,
}: PinCardProps) {
  const { data: session } = useSession();
  const router = useRouter();
  const [liked, setLiked] = useState(isLiked);
  const [likeCount, setLikeCount] = useState(likes);
  const [saved, setSaved] = useState(isSaved);
  const [isAnimating, setIsAnimating] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const { t } = useI18n();

  const requireAuth = useCallback((action: () => void) => {
    if (!session) {
      router.push('/login');
      return;
    }
    action();
  }, [session, router]);

  const handleLike = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    requireAuth(async () => {
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 400);
      const newLiked = !liked;
      setLiked(newLiked);
      setLikeCount(c => newLiked ? c + 1 : c - 1);
      try {
        await fetch(`/api/posts/${id}/tick`, {
          method: newLiked ? 'POST' : 'DELETE',
        });
      } catch {
        setLiked(!newLiked);
        setLikeCount(c => newLiked ? c - 1 : c + 1);
      }
    });
  }, [liked, id, requireAuth]);

  const handleSave = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    requireAuth(async () => {
      const newSaved = !saved;
      setSaved(newSaved);
      toast.success(newSaved ? t.post.saved : t.post.unsaved);
      try {
        await fetch('/api/bookmarks', {
          method: newSaved ? 'POST' : 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ objectId: id, objectType: 1 }),
        });
      } catch {
        setSaved(!newSaved);
      }
    });
  }, [saved, id, requireAuth]);

  const formatNumber = (n: number) => {
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toString();
  };

  if (imgError) return <div className="masonry-item hidden" />;

  return (
    <div className="masonry-item group">
      <Link href={`/p/${id}`} className="block relative rounded-2xl overflow-hidden bg-surface-800">
        {/* Image container — ảnh luôn nằm sẵn trong HTML (kể cả server-rendered),
            để Googlebot đọc được <img> mà không cần cuộn trang để kích hoạt JS.
            Hiệu năng vẫn được tối ưu nhờ loading="lazy" gốc của trình duyệt. */}
        <div
          className="relative overflow-hidden"
          // Giữ không gian (tránh layout shift) bằng padding-bottom trick
          // Chỉ áp dụng khi chưa load xong
          style={!isLoaded ? { paddingBottom: `${(1 / aspectRatio) * 100}%` } : undefined}
        >
          {/* Skeleton shimmer — hiện khi chưa load */}
          {!isLoaded && (
            <div className="absolute inset-0 bg-surface-700 animate-pulse">
              {/* Shimmer effect */}
              <div
                className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite]"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)',
                }}
              />
            </div>
          )}

          {/* Luôn render Image trong DOM (SSR + client) — không còn gate bằng
              IntersectionObserver. loading="lazy" (native) vẫn trì hoãn tải ảnh
              thật cho trình duyệt, nhưng thẻ <img src="..."> vẫn có mặt trong
              HTML ban đầu để Googlebot/crawler đọc được. */}
          <Image
            src={thumb || src}
            alt={title}
            width={400}
            height={Math.round(400 / aspectRatio)}
            className={cn(
              'w-full h-auto object-cover transition-all duration-500 group-hover:scale-105',
              // Fade in khi ảnh load xong
              isLoaded ? 'opacity-100' : 'opacity-0 absolute inset-0'
            )}
            sizes="(max-width: 480px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, 20vw"
            // priority=true cho above-the-fold cards, còn lại lazy (native browser lazy-load)
            priority={priority}
            loading={priority ? 'eager' : 'lazy'}
            placeholder="blur"
            onLoad={() => setIsLoaded(true)}
            onError={() => setImgError(true)}
            blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAgGBgcGBQgHBwcJCQgKDBQNDAsLDBkSEw8UHRofHh0aHBwgJC4nICIsIxwcKDcpLDAxNDQ0Hyc5PTgyPC4zNDL/2wBDAQkJCQwLDBgNDRgyIRwhMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjL/wAARCAAKAAoDASIAAhEBAxEB/8QAFgABAQEAAAAAAAAAAAAAAAAABQME/8QAIRAAAQMEAgMAAAAAAAAAAAAAAQIDBAAFERIhMVH/xAAUAQEAAAAAAAAAAAAAAAAAAAAA/8QAFBEBAAAAAAAAAAAAAAAAAAAAAP/aAAwDAQACEQMRAD8AoNtudvj2uVHMx5AeSDstpJWVHHQAJJzWnF1RdJV0ahx7k40hxYQEpYbIJJwBkjJqtSlAH//Z"
          />

          {/* Overlay phụ (badge, nút like/save) vẫn chỉ hiện sau khi ảnh đã load xong,
              không ảnh hưởng tới việc ảnh có mặt trong DOM hay không */}

          {/* Hover overlay — chỉ hiện khi ảnh đã load */}
          {isLoaded && (
            <>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pin-overlay" />

              {/* Views badge */}
              <div className="absolute top-2.5 left-2.5 bg-black/60 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {formatNumber(views)}
              </div>

              {/* Action buttons */}
              <div className="absolute bottom-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                <button
                  onClick={handleSave}
                  className={cn(
                    'w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 shadow-lg',
                    saved
                      ? 'bg-brand-500 text-white'
                      : 'bg-black/70 backdrop-blur-sm text-white hover:bg-brand-500'
                  )}
                  title={saved ? t.post.unsaved : t.post.saved}
                >
                  <svg className="w-4 h-4" fill={saved ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                </button>

                <button
                  onClick={handleLike}
                  className={cn(
                    'w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 shadow-lg',
                    isAnimating && 'scale-125',
                    liked
                      ? 'bg-brand-500 text-white'
                      : 'bg-black/70 backdrop-blur-sm text-white hover:bg-brand-500'
                  )}
                  title={liked ? t.post.unlike : t.post.like}
                >
                  <svg className="w-4 h-4" fill={liked ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </button>
              </div>

              {likeCount > 0 && (
                <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  ♥ {formatNumber(likeCount)}
                </div>
              )}
            </>
          )}
        </div>

        {/* Info below image */}
        <div className="p-3">
          <p className="font-medium text-sm text-white leading-tight line-clamp-2">{title}</p>
          {tag && <p className="text-brand-400 text-xs mt-1 font-medium">{tag}</p>}
          {author && (
            <div className="flex items-center gap-2 mt-2">
              <div className="w-5 h-5 rounded-full bg-gradient-brand flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                {author.name[0]}
              </div>
              <span className="text-gray-500 text-xs truncate">{author.name}</span>
            </div>
          )}
        </div>
      </Link>
    </div>
  );
}